import React, {useState, useMemo, useEffect} from "react"
import {Dropdown} from "primereact/dropdown"
import useSWR from "swr"
import {default as cx} from "classnames"
import SubHeader from "../components/SubHeader"
import EducatorPreceptorAccordionRow from "../components/educator/EducatorPreceptorAccordionRow"
import {fetcherGet} from "../utilities/general.jsx"

const SORT_OPTIONS = [
    {label: "All", value: "All"},
    {label: "Alphabetically (A-Z)", value: "Alphabetically (A-Z)"},
    {label: "Alphabetically (Z-A)", value: "Alphabetically (Z-A)"},
    {label: "Grade (H)", value: "Grade"},
]

function mapApiPreceptor(p) {
    return {
        id: p.id,
        name: p.name,
        unit: p.units,
        details: p.details,
    }
}

export default function EducatorPreceptorMetrics() {
    const [avgPreceptorGrade, setAvgPreceptorGrade] = useState(null)
    const [successRateData, setSuccessRateData] = useState(null)
    const [query, setQuery] = useState("")
    const [sortBy, setSortBy] = useState("All")

    const {
        data: preceptorsData,
        error: preceptorsError,
        isLoading: loading,
    } = useSWR("/educators/educator-preceptors", fetcherGet, {
        fallbackData: [],
    })

    useEffect(() => {
        fetcherGet("/educators/educator-preceptor-average-grade")
            .then((data) => {
                setAvgPreceptorGrade(data.average_preceptor_grade)
            })
            .catch(() => {
                console.error("Error when fetching average preceptor grade")
                setAvgPreceptorGrade(null)
            })
    }, [])

    useEffect(() => {
        fetcherGet("/educators/orientee-success-rate")
            .then((data) => {
                setSuccessRateData(data)
            })
            .catch(() => {
                console.error("Error when fetching average orientee success rate")
                setSuccessRateData(null)
            })
    }, [])

    const preceptors = useMemo(
        () => (preceptorsData?.preceptors ?? preceptorsData ?? []).map(mapApiPreceptor),
        [preceptorsData],
    )

    const error = preceptorsError ? "Unable to load preceptors. Please try again." : null

    const filtered = useMemo(() => {
        let list = preceptors.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()))

        switch (sortBy) {
            case "Alphabetically (A-Z)":
                list = [...list].sort((a, b) => a.name.localeCompare(b.name))
                break
            case "Alphabetically (Z-A)":
                list = [...list].sort((a, b) => b.name.localeCompare(a.name))
                break
            case "Grade":
                list = [...list].sort((a, b) => b.grade - a.grade)
                break
            default:
                break
        }
        return list
    }, [preceptors, query, sortBy])

    return (
        <>
            <SubHeader title1="My Preceptors" title2="" icon="fa-hospital-user" />

            <div className="container d-flex flex-column">
                <div className="app-main flex-column flex-row-fluid py-5">
                    <div className="app-wrapper flex-column flex-row-fluid gap-3">
                        {/* Search + Sort */}
                        <div className="card mt-4 border border-gray-4000">
                            <div className="card-body p-4">
                                <div className="d-none d-lg-block">
                                    <div className="row g-8">
                                        <div className="col-12 col-md-6">
                                            <div className="d-flex flex-column">
                                                <label className="fs-6 form-label fw-bold text-dark">
                                                    Search Preceptors&nbsp;
                                                </label>
                                                <div className="position-relative">
                                                    <div className="d-flex align-items-center position-relative gap-3">
                                                        <i className="fas fa-magnifying-glass fs-3 position-absolute ms-4 text-gray-600"></i>
                                                        <input
                                                            id="preceptor-search"
                                                            type="text"
                                                            value={query}
                                                            onChange={(e) => setQuery(e.target.value)}
                                                            className="form-control form-control-solid fw-bold ps-12"
                                                            placeholder="Enter Name"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label">Sort By</label>
                                            <Dropdown
                                                className={cx("w-100 p-dropdown-tall")}
                                                options={SORT_OPTIONS}
                                                name="sortBy"
                                                filter
                                                value={sortBy}
                                                placeholder="All"
                                                onChange={(e) => setSortBy(e.value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Mobile compact search */}
                                <div className="d-block d-lg-none">
                                    <div className="row g-8 d-flex">
                                        <div className="col-9">
                                            <div className="d-flex flex-column">
                                                <div className="position-relative">
                                                    <div className="d-flex align-items-center position-relative gap-3">
                                                        <i className="ki-outline ki-magnifier fs-3 position-absolute ms-4" />
                                                        <input
                                                            type="text"
                                                            value={query}
                                                            onChange={(e) => setQuery(e.target.value)}
                                                            className="form-control form-control-solid fw-bold ps-12"
                                                            placeholder="Search by Name"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-3 d-flex flex-end">
                                            <a
                                                href="#filter"
                                                className="btn btn-light-primary"
                                                data-bs-toggle="modal"
                                                data-bs-target="#filter"
                                            >
                                                Filters
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Stat cards */}
                        <div className="row mt-4">
                            <div className="col-md-6 col-12">
                                <div className="border border-gray-4000 bg-white rounded flex-fill py-3 px-4">
                                    <div className="d-flex align-items-center">
                                        <div className="fs-2 fw-bold">
                                            {avgPreceptorGrade === null ? "N/A" : `${avgPreceptorGrade}%`}
                                        </div>
                                    </div>
                                    <div className="fw-semibold fs-6 text-gray-700">Average Preceptor Grade</div>
                                </div>
                            </div>
                            <div className="col-md-6 col-12">
                                <div className="border border-gray-4000 bg-white rounded flex-fill py-3 px-4">
                                    <div className="d-flex align-items-center">
                                        <div className="fs-2 fw-bold">
                                            {successRateData?.success_rate == null
                                                ? "N/A"
                                                : `${successRateData.success_rate}%`}
                                        </div>
                                    </div>
                                    <div className="fw-semibold fs-6 text-gray-700">Average Orientee Success Rate</div>
                                </div>
                            </div>
                        </div>

                        {/* Preceptor accordion list */}
                        {loading ? (
                            <div className="card mt-4 border border-gray-4000">
                                <div className="card-body text-center text-gray-600 py-6">
                                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                                    Loading preceptors…
                                </div>
                            </div>
                        ) : error ? (
                            <div className="card mt-4 border border-gray-4000">
                                <div className="card-body text-center text-danger py-6">{error}</div>
                            </div>
                        ) : filtered.length > 0 ? (
                            filtered.map((p) => <EducatorPreceptorAccordionRow key={p.id} preceptor={p} />)
                        ) : (
                            <div className="card mt-4 border border-gray-4000">
                                <div className="card-body text-center text-gray-600 py-6">
                                    {preceptors.length === 0
                                        ? "You don't have any preceptors assigned yet."
                                        : `No preceptors match "${query}".`}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}
